// Money / Chit investment detail — full-screen repayment tracking for one
// chit fund or private borrow: header + principal/paid/remaining, a big
// progress bar, the payments ledger (delete each), and an add-payment form.
// Component-local state (no redux), loads via moneyApi.getDetail(id).

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiRefreshCw,
  FiTrash2,
  FiPlus,
  FiAlertTriangle,
  FiCalendar,
  FiEdit3,
  FiTrendingUp,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { moneyApi } from '../api/moneyApi';
import MoneyForm from '../components/money/MoneyForm.jsx';

const fmt = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('en-IN') : value || '0';
};

const todayIso = () => new Date().toISOString().slice(0, 10);

// ── Add-payment form ──────────────────────────────────────────────────────────
const AddPaymentForm = ({ onPay, busy }) => {
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(todayIso());
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');

  const submit = (e) => {
    e.preventDefault();
    const a = Number(amount);
    if (!Number.isFinite(a) || a <= 0) { setErr('Enter a valid amount'); return; }
    setErr('');
    onPay({ amount: a, paid_at: paidAt || undefined, note: note.trim() || undefined }, () => {
      setAmount(''); setNote(''); setPaidAt(todayIso());
    });
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="mb-3 text-sm font-bold text-slate-800">Record a repayment</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" min="0" step="any" inputMode="numeric" placeholder="Amount" disabled={busy}
            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-3 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-200" />
        </div>
        <input value={paidAt} onChange={(e) => setPaidAt(e.target.value)} type="date" disabled={busy}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-200" />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" disabled={busy}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-200" />
      </div>
      {err && <p className="mt-2 text-xs font-semibold text-rose-500">{err}</p>}
      <div className="mt-3 flex justify-end">
        <button type="submit" disabled={busy}
          className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
          <FiPlus className="h-3.5 w-3.5" /> {busy ? 'Saving…' : 'Add repayment'}
        </button>
      </div>
    </form>
  );
};

const MoneyInvestmentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyPayId, setBusyPayId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await moneyApi.getDetail(id);
      setInv(data);
      setPnl(data?.pnl || null);
      setError('');
    } catch {
      setError('Could not load this investment');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const pay = async (body, done) => {
    setBusy(true);
    try {
      const { data } = await moneyApi.pay(id, body);
      if (data?.pnl) setPnl(data.pnl);
      setError('');
      done && done();
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not record the repayment');
    } finally {
      setBusy(false);
    }
  };

  const removePayment = async (paymentId) => {
    setBusyPayId(paymentId);
    try {
      const { data } = await moneyApi.removePayment(id, paymentId);
      if (data?.pnl) setPnl(data.pnl);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not delete the repayment');
    } finally {
      setBusyPayId(null);
    }
  };

  // Fix an entry saved wrong — same form as adding, prefilled.
  const saveEdit = async (body) => {
    setBusy(true);
    try {
      await moneyApi.update(id, body);
      setEditing(false);
      setError('');
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not save the changes');
    } finally {
      setBusy(false);
    }
  };

  const removeInvestment = async () => {
    if (!window.confirm('Delete this investment and all its repayments? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await moneyApi.remove(id);
      navigate('/money-investments');
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not delete the investment');
      setDeleting(false);
    }
  };

  const p = pnl || inv || {};
  const progress = Math.max(0, Math.min(Number(p.progress_pct) || 0, 100));
  const payments = pnl?.payments || [];

  return (
    <MainLayout>
      <div className="space-y-5 pb-8">
        {/* Header bar */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/money-investments')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition-all hover:border-violet-300 hover:bg-violet-50 hover:text-violet-600">
            <FiArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-bold text-slate-900 sm:text-xl">{inv ? inv.name : 'Investment'}</h1>
            <p className="truncate text-sm text-slate-500">
              {inv ? (inv.schedule_text || inv.frequency_label || `#${inv.id}`) : (loading ? 'Loading…' : ' ')}
            </p>
          </div>
          <button onClick={load} disabled={loading} title="Refresh"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-400 shadow-sm transition-all hover:border-violet-300 hover:bg-violet-50 hover:text-violet-600 disabled:opacity-50">
            <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-600">{error}</div>
        )}

        {loading && !inv && (
          <div className="flex flex-col items-center gap-3 py-24">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
            <p className="text-sm text-slate-400">Loading investment…</p>
          </div>
        )}

        {inv && (
          <div className="space-y-5">
            {/* Hero */}
            <div className="rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-700 p-5 text-white sm:p-6">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-white/15">
                <FiTrendingUp className="h-5 w-5" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-wider text-violet-200">
                Chit / Money · #{inv.id}
              </p>
              <h2 className="mt-1 text-2xl font-bold">{inv.name}</h2>
              {inv.source && <p className="mt-1 text-sm text-violet-100">{inv.source}</p>}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-semibold text-white">
                  {inv.schedule_text || inv.frequency_label}
                </span>
                <span className="inline-flex items-center rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-semibold text-white capitalize">
                  {inv.status === 'closed' ? 'Closed' : 'Active'}
                </span>
                {inv.start_date_display && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-semibold text-white">
                    <FiCalendar className="h-3 w-3" /> From {inv.start_date_display}
                  </span>
                )}
              </div>
            </div>

            {/* Principal / paid / remaining */}
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="mb-1 text-xs text-slate-400">Principal</p>
                <p className="text-lg font-bold text-slate-800">₹{fmt(p.principal)}</p>
              </div>
              <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4 shadow-sm">
                <p className="mb-1 text-xs text-emerald-600">Paid back</p>
                <p className="text-lg font-bold text-emerald-700">₹{fmt(p.paid)}</p>
              </div>
              <div className="rounded-xl border border-amber-100 bg-amber-50 p-4 shadow-sm">
                <p className="mb-1 text-xs text-amber-600">Remaining</p>
                <p className="text-lg font-bold text-amber-700">₹{fmt(p.remaining)}</p>
              </div>
            </div>

            {/* Progress + next due */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-slate-800">Repayment progress</p>
                {p.fully_paid ? (
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-bold text-emerald-700">FULLY PAID</span>
                ) : p.overdue ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-0.5 text-xs font-bold text-rose-600">
                    <FiAlertTriangle className="h-3 w-3" /> Overdue{p.next_due_display ? ` · ${p.next_due_display}` : ''}
                  </span>
                ) : p.next_due_display ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-semibold text-slate-500">
                    <FiCalendar className="h-3 w-3" /> Next: {p.next_due_display}
                  </span>
                ) : null}
              </div>
              <div className="mt-3">
                <div className="flex items-baseline justify-between text-xs">
                  <span className="font-semibold text-slate-500">Paid ₹{fmt(p.paid)}</span>
                  <span className="text-slate-400">of ₹{fmt(p.principal)} ({Math.round(progress)}%)</span>
                </div>
                <div className="mt-1.5 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className={`h-full rounded-full transition-all ${p.fully_paid ? 'bg-emerald-500' : 'bg-violet-500'}`} style={{ width: `${progress}%` }} />
                </div>
                {(p.installment_count != null) && (
                  <p className="mt-2 text-[11px] font-medium text-slate-400">
                    {p.installments_paid ?? 0} of {p.installment_count} installments paid
                    {p.installment_amount ? ` · ₹${fmt(p.installment_amount)} each` : ''}
                  </p>
                )}
              </div>
            </div>

            {/* Fix a wrong entry — same form as adding, prefilled */}
            {editing ? (
              <MoneyForm
                initial={inv}
                onSave={saveEdit}
                onCancel={() => setEditing(false)}
                busy={busy}
              />
            ) : (
              <AddPaymentForm onPay={pay} busy={busy} />
            )}

            {/* Ledger */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="mb-3 text-sm font-bold text-slate-800">Repayments ledger</p>
              {payments.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400">No repayments recorded yet.</p>
              ) : (
                <div className="space-y-2">
                  {payments.map((pay_) => (
                    <div key={pay_.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                          ₹{fmt(pay_.amount)}
                          {pay_.seq != null && (
                            <span className="rounded-full border border-violet-200 bg-violet-50 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-violet-600">
                              #{pay_.seq}
                            </span>
                          )}
                        </p>
                        <p className="truncate text-[11px] text-slate-400">{pay_.paid_at_display || pay_.paid_at}{pay_.note ? ` · ${pay_.note}` : ''}</p>
                      </div>
                      <button onClick={() => removePayment(pay_.id)} disabled={busyPayId === pay_.id}
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-300 hover:bg-rose-50 hover:text-rose-500 disabled:opacity-50" title="Delete repayment">
                        <FiTrash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Note + danger zone */}
            {inv.note && (
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="mb-1 text-xs text-slate-400">Note</p>
                <p className="text-sm text-slate-700">{inv.note}</p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing((v) => !v)} disabled={deleting || busy}
                className={`flex items-center gap-1.5 rounded-lg border px-4 py-2 text-xs font-bold disabled:opacity-50 ${editing ? 'border-slate-300 bg-slate-50 text-slate-600' : 'border-violet-200 text-violet-600 hover:bg-violet-50'}`}>
                <FiEdit3 className="h-3.5 w-3.5" /> {editing ? 'Cancel edit' : 'Edit details'}
              </button>
              <button onClick={removeInvestment} disabled={deleting}
                className="flex items-center gap-1.5 rounded-lg border border-rose-200 px-4 py-2 text-xs font-bold text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                <FiTrash2 className="h-3.5 w-3.5" /> {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default MoneyInvestmentDetailPage;
